import React from "react";
import {
  CheckCircle2,
  Clock,
  LogIn,
  Phone,
  Receipt,
  Scissors,
  Trash2,
  UserCheck,
  XCircle,
} from "lucide-react";

/**
 * Shared booking actions (calendar popover + desk bottom sheet).
 * Touch-friendly row buttons (min ~44px tap height).
 */
export default function BookingQuickActions({
  b,
  lang,
  t,
  isBookingInvoiced,
  onClose,
  onOpenEdit,
  onChangeStatus,
  onCheckIn,
  onStartService,
  onDelete,
  className = "",
  buttonClassName = "w-full px-4 py-3 text-start text-sm hover:bg-stone-50 flex items-center gap-3 min-h-[44px]",
}) {
  if (!b) return null;

  if (isBookingInvoiced(b)) {
    return (
      <div className={className} data-testid={`quick-actions-${b.booking_id}`}>
        <div className="px-4 py-3 text-sm text-emerald-700 bg-emerald-50 border-b border-emerald-100 flex items-center gap-2">
          <Receipt size={16} />
          <span>
            {t("booking_invoiced")}
            {b.invoice_number ? ` · #${b.invoice_number}` : ""}
          </span>
        </div>
        <button
          type="button"
          onClick={() => { onClose?.(); onOpenEdit(b); }}
          className={buttonClassName}
        >
          <Clock size={16} />
          {lang === "ar" ? "عرض التفاصيل" : "View details"}
        </button>
        {b.customer_phone && (
          <a
            href={`https://wa.me/${(b.customer_phone || "").replace(/[^0-9]/g, "")}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => onClose?.()}
            className={`${buttonClassName} hover:bg-emerald-50 text-emerald-700`}
          >
            <Phone size={16} />
            {lang === "ar" ? "تواصل عبر واتساب" : "WhatsApp"}
          </a>
        )}
      </div>
    );
  }

  return (
    <div className={className} data-testid={`quick-actions-${b.booking_id}`}>
      {b.status !== "completed" && (
        <button
          type="button"
          onClick={() => { onClose?.(); onChangeStatus("completed", b.booking_id); }}
          data-testid={`sa-complete-${b.booking_id}`}
          className={`${buttonClassName} hover:bg-green-50 text-green-700`}
        >
          <CheckCircle2 size={16} />
          {lang === "ar" ? "إتمام الزيارة" : "Mark Completed"}
        </button>
      )}
      {!b.arrived_at && b.status !== "cancelled" && (
        <button
          type="button"
          onClick={() => onCheckIn(b.booking_id)}
          data-testid={`sa-checkin-${b.booking_id}`}
          className={`${buttonClassName} hover:bg-amber-50 text-amber-800`}
        >
          <LogIn size={16} />
          {t("customer_arrived")}
        </button>
      )}
      {b.arrived_at && !b.service_started_at && b.status !== "cancelled" && (
        <button
          type="button"
          onClick={() => onStartService(b.booking_id)}
          data-testid={`sa-start-service-${b.booking_id}`}
          className={`${buttonClassName} hover:bg-violet-50 text-violet-800`}
        >
          <Scissors size={16} />
          {t("service_started")}
        </button>
      )}
      {b.status !== "confirmed" && (
        <button
          type="button"
          onClick={() => { onClose?.(); onChangeStatus("confirmed", b.booking_id); }}
          className={`${buttonClassName} hover:bg-blue-50 text-blue-700`}
        >
          <UserCheck size={16} />
          {lang === "ar" ? "تأكيد" : "Confirm"}
        </button>
      )}
      <button
        type="button"
        onClick={() => { onClose?.(); onOpenEdit(b); }}
        data-testid={`sa-reschedule-${b.booking_id}`}
        className={buttonClassName}
      >
        <Clock size={16} />
        {lang === "ar" ? "تعديل / تغيير الموعد" : "Reschedule / Edit"}
      </button>
      {b.customer_phone && (
        <a
          href={`https://wa.me/${(b.customer_phone || "").replace(/[^0-9]/g, "")}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => onClose?.()}
          className={`${buttonClassName} hover:bg-emerald-50 text-emerald-700`}
        >
          <Phone size={16} />
          {lang === "ar" ? "تواصل عبر واتساب" : "WhatsApp"}
        </a>
      )}
      {b.status !== "cancelled" && (
        <button
          type="button"
          onClick={() => { onClose?.(); onChangeStatus("cancelled", b.booking_id); }}
          data-testid={`sa-cancel-${b.booking_id}`}
          className={`${buttonClassName} hover:bg-rose-50 text-rose-700 border-t border-stone-100`}
        >
          <XCircle size={16} />
          {lang === "ar" ? "إلغاء" : "Cancel"}
        </button>
      )}
      <button
        type="button"
        onClick={() => { onClose?.(); onDelete(b.booking_id); }}
        className={`${buttonClassName} hover:bg-rose-100 text-rose-800`}
      >
        <Trash2 size={16} />
        {lang === "ar" ? "حذف" : "Delete"}
      </button>
    </div>
  );
}
